<template>
  <div class="card events-combined-card">
    <!-- Event Reservation Section -->
    <div class="card-section event-reservation-section">
      <div class="card-header">
        <h3 class="card-title">
          {{ $t('dashboard.events.reservation_title') }}
          <span class="subtitle">{{ $t('dashboard.events.reservation_subtitle') }}</span>
        </h3>
      </div>
      <div class="card-body">
        <EventUserAttributesChart
          :repeater-only-rate="repeaterOnlyRate"
          :stockholder-only-rate="stockholderOnlyRate"
          :both-rate="bothRate"
          :other-rate="otherRate"
          :repeater-only-percentage="repeaterOnlyPercentage"
          :stockholder-only-percentage="stockholderOnlyPercentage"
          :both-percentage="bothPercentage"
          :other-percentage="otherPercentage"
          :repeater-only-count="repeaterOnlyCount"
          :stockholder-only-count="stockholderOnlyCount"
          :both-count="bothCount"
          :other-count="otherCount"
          :total-capacity="totalCapacity"
          :event-reservation-count="eventReservationCount"
        />
      </div>
    </div>

    <!-- Top Events Section -->
    <div class="card-section top-events-section">
      <div class="card-header">
        <h3 class="card-title">
          {{ $t('dashboard.events.top_events') }}
          <span class="d-inline-block" tabindex="0" data-bs-toggle="popover" data-bs-trigger="hover focus" :data-bs-content="$t('dashboard.tooltips.topEvents')">
            <i class="bi bi-question-circle ms-2 help-icon"></i>
          </span>
        </h3>
      </div>
      <div class="card-body">
        <TopEventsTable :events="topEvents" />
      </div>
    </div>

    <!-- Event List Link -->
    <div class="card-footer">
      <a href="#" class="event-list-link" @click.prevent="handleEventListLinkClick">
        {{ $t('dashboard.events.event_list_link') }} ›
      </a>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import EventUserAttributesChart from './EventUserAttributesChart.vue';
import TopEventsTable from './TopEventsTable.vue';
import { colors } from '../../../../consts/colors';

const router = useRouter();

const props = defineProps({
  dateRange: {
    type: Object,
    required: true,
    validator: (value) => {
      return value.startDate instanceof Date && value.endDate instanceof Date;
    }
  }
});

// Event user attributes data
const repeaterOnlyCount = ref(0);
const repeaterOnlyPercentage = ref(0);
const repeaterOnlyRate = ref(0);
const stockholderOnlyCount = ref(0);
const stockholderOnlyPercentage = ref(0);
const stockholderOnlyRate = ref(0);
const bothCount = ref(0);
const bothPercentage = ref(0);
const bothRate = ref(0);
const otherCount = ref(0);
const otherPercentage = ref(0);
const otherRate = ref(0);
const eventReservationCount = ref(0);
const totalCapacity = ref(0);

// Top events data
const topEvents = ref([]);

// Fetch event user attributes data
const fetchEventUserAttributes = async () => {
  try {
    const startDate = props.dateRange.startDate.toISOString().split('T')[0];
    const endDate = props.dateRange.endDate.toISOString().split('T')[0];

    const response = await axios.get('/api/user/statistics/dashboard/event-user-attributes', {
      params: {
        start_date: startDate,
        end_date: endDate
      }
    });


    // Convert all numeric values to Number to match prop types
    // Use percentage values for rate props (API doesn't return separate rate fields)
    repeaterOnlyRate.value = Number(response.data.repeater_only_percentage) || 0;
    repeaterOnlyPercentage.value = Number(response.data.repeater_only_percentage) || 0;
    repeaterOnlyCount.value = Number(response.data.repeater_only_count) || 0;

    stockholderOnlyRate.value = Number(response.data.stockholder_only_percentage) || 0;
    stockholderOnlyPercentage.value = Number(response.data.stockholder_only_percentage) || 0;
    stockholderOnlyCount.value = Number(response.data.stockholder_only_count) || 0;

    bothRate.value = Number(response.data.both_percentage) || 0;
    bothPercentage.value = Number(response.data.both_percentage) || 0;
    bothCount.value = Number(response.data.both_count) || 0;

    otherRate.value = Number(response.data.other_percentage) || 0;
    otherPercentage.value = Number(response.data.other_percentage) || 0;
    otherCount.value = Number(response.data.other_count) || 0;

    eventReservationCount.value = Number(response.data.event_reservation_count) || 0;
    totalCapacity.value = Number(response.data.total_capacity) || 0;
  } catch (error) {
    console.error('Failed to fetch event user attributes:', error);
    // Reset to zero on error
    repeaterOnlyCount.value = 0;
    repeaterOnlyPercentage.value = 0;
    repeaterOnlyRate.value = 0;
    stockholderOnlyCount.value = 0;
    stockholderOnlyPercentage.value = 0;
    stockholderOnlyRate.value = 0;
    bothCount.value = 0;
    bothPercentage.value = 0;
    bothRate.value = 0;
    otherCount.value = 0;
    otherPercentage.value = 0;
    otherRate.value = 0;
    eventReservationCount.value = 0;
    totalCapacity.value = 0;
  }
};

// Fetch top events data
const fetchTopEvents = async () => {
  try {
    const startDate = props.dateRange.startDate.toISOString().split('T')[0];
    const endDate = props.dateRange.endDate.toISOString().split('T')[0];

    const response = await axios.get('/api/user/statistics/dashboard/events', {
      params: {
        start_date: startDate,
        end_date: endDate,
        limit: 5
      }
    });

    topEvents.value = response.data.events || [];
  } catch (error) {
    console.error('Failed to fetch top events:', error);
    topEvents.value = [];
  }
};

// Watch for date range changes
watch(() => props.dateRange, () => {
  fetchEventUserAttributes();
  fetchTopEvents();
}, { deep: true });

// Fetch data on mount
onMounted(() => {
  fetchEventUserAttributes();
  fetchTopEvents();
});

// Format date to YYYY-MM-DD using local timezone
const formatDateForQuery = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const handleEventListLinkClick = () => {
  // Navigate to event list page with date range as query params
  const startDate = formatDateForQuery(props.dateRange.startDate);
  const endDate = formatDateForQuery(props.dateRange.endDate);

  router.push({
    name: 'events',
    query: {
      start_date: startDate,
      end_date: endDate
    }
  });
};
</script>

<style lang="scss" scoped>
.card {
  background: v-bind('colors.white');
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px v-bind('colors.shadowColor');
  margin-bottom: 16px;
}

.events-combined-card {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  height: 100%;

  .card-section {
    &.event-reservation-section {
      border-bottom: 1px solid v-bind('colors.gray[200]');
      padding-bottom: 24px;
    }
  }

  .card-header {
    background-color: v-bind('colors.white');
    border: none;
    padding: 0;
    margin-bottom: 16px;
  }

  .card-body {
    padding: 0;
  }

  .card-title {
    font-size: 18px;
    font-weight: 600;
    color: v-bind('colors.textPrimary');
    margin: 0;

    .subtitle {
      display: block;
      font-size: 12px;
      font-weight: 400;
      color: v-bind('colors.gray[400]');
      margin-top: 4px;
    }
  }

  .help-icon {
    font-size: 18px;
    color: v-bind('colors.textLight');
    cursor: pointer;
  }

  .card-footer {
    display: flex;
    justify-content: flex-end;
    margin-top: auto;
    padding-top: 16px;
    border-top: none;
    background: none;
  }

  .event-list-link {
    color: v-bind('colors.blue[500]');
    font-size: 14px;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
